import { ArrowLeft } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Loader } from "@/components/common/loader";
import { Button } from "@/components/ui/button";
import { getOrCreateSubmission, getSubmission, listTaxForms } from "@/features/forms/api";
import type { FormSubmission } from "@/features/forms/types";
import { SpouseReliefWizard } from "@/features/workflows/spouse-relief/spouse-relief-wizard";
import { useActiveClient } from "@/layouts/client-layout";
import { ApiError } from "@/lib/api";

/**
 * Spouse-Relief workflow (`/client/:id/workflows/spouse-relief`): resolves (or
 * creates) the client's single Form 8857 submission and renders the 7-tab wizard
 * over it. The same wizard is the plain 8857 editor at `/client/:id/irs-forms/8857`
 * and the case-scoped variant at `/client/:id/cases/:caseId/workflow/spouse-relief`
 * — all views of this one record. Part IV assets need a Case, so this
 * (client-scoped) variant shows a nudge there. Honors `?part=1…7`.
 */
export default function ClientSpouseReliefWorkflowPage() {
  const { client } = useActiveClient();
  const [submission, setSubmission] = useState<FormSubmission | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    listTaxForms()
      .then((forms) => {
        const taxForm = forms.find((f) => f.formNumber === "8857");
        if (!taxForm) throw new ApiError(404, "Form 8857 is not available.", null);
        return getOrCreateSubmission(client.id, taxForm.id);
      })
      .then((sub) => getSubmission(sub.id))
      .then((sub) => {
        if (mountedRef.current) setSubmission(sub);
      })
      .catch((err) => {
        if (mountedRef.current) {
          setError(err instanceof ApiError ? err.message : "Couldn't open this workflow.");
        }
      })
      .finally(() => {
        if (mountedRef.current) setLoading(false);
      });
  }, [client.id]);

  useEffect(load, [load]);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center gap-3">
        <Button asChild variant="ghost" size="sm" className="-ml-2">
          <Link to={`/client/${client.id}/cases`}>
            <ArrowLeft /> Back to Cases
          </Link>
        </Button>
        <h2 className="text-foreground text-2xl font-bold">Spouse Relief (Form 8857)</h2>
      </div>
      <p className="text-muted-foreground -mt-2 text-sm">
        Guided Form 8857 (Request for Innocent Spouse Relief) questionnaire for {client.name}.
      </p>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader />
        </div>
      ) : error || !submission ? (
        <p className="text-destructive py-8 text-sm">{error ?? "Couldn't open this workflow."}</p>
      ) : (
        <SpouseReliefWizard
          client={client}
          submission={submission}
          onSubmissionChange={setSubmission}
        />
      )}
    </div>
  );
}
